import { toast } from '@utils/functions/notificationToast'

import { usePeople } from '.'

import type { AxiosError } from 'axios'
import type {
  GetPeoplePreviewDetailsRequestProps,
  EditDetailedSummaryPayloadProps,
  DetailedSummaryApiResponseProps,
  DetailedSummaryResponseProps,
  GetCollaboratorsListRequestProps,
  GetCollaboratorDetailsRequestProps,
  CollaboratorDetailsResponseProps,
  GetCollaboratorsTotalRequestProps,
  GetCollaboratorsIndicatorsRequestProps,
  RescissionSimulationRequestProps,
  RescissionSimulationResponseProps,
  AdmissionSimulationRequestProps,
  SimulationListItemProps,
  GeneralAdjustPayloadProps
} from './types'
import { convertDateToIsoDate } from '@pages/People/components/modals/RescissionSimulationModal/helpers/dateConverter'

import * as apiPeople from '@services/api/people'

const getPeoplePreviewDetails = async (params: GetPeoplePreviewDetailsRequestProps) => {
  const { setBigNumbersData, setIsBigNumbersLoading, setWeeklyPreviewData, setIsWeeklyPreviewLoading } =
    usePeople.getState()

  setIsBigNumbersLoading(true)
  setIsWeeklyPreviewLoading(true)

  try {
    const data = await apiPeople.getPeoplePreviewDetails(params)

    setBigNumbersData({
      budgetAmount: data.budgetAmount,
      budgetPercentage: data.budgetPercentage,
      previewCurrentAmount: data.previewCurrentAmount,
      previewCurrentPercentage: data.previewCurrentPercentage,
      deviationValue: data.deviationValue ?? 0,
      deviationPercentage: data.deviationPercentage,
      lastUpdateDate: null,
      currentPreviewNumber: data.currentPreviewNumber,
      canEdit: data.canEdit,
      allowReleasePreview: data.allowReleasePreview,
      allowRevertPreview: data.allowRevertPreview,
      corporateFoodSegmentMessage: data.corporateFoodSegmentMessage ?? null
    })

    setWeeklyPreviewData({
      items: data.previews.map((item) => ({
        previewNumber: item.previewNumber,
        previewBillingAmount: item.previewAmount,
        deviationPercentage: item.deviationPercentage,
        deviationValue: item.deviationAmount ?? 0,
        updatedAt: item.updatedAt,
        previewStatus: item.previewStatus
      }))
    })
  } catch (error) {
    const errorMessage =
      (error as AxiosError<{ errorMessage: string[] }>)?.response?.data?.errorMessage[0] ||
      'Falha ao obter os dados de prévia do Pessoal!'

    toast.error(errorMessage, (error as AxiosError)?.status)
  } finally {
    setIsBigNumbersLoading(false)
    setIsWeeklyPreviewLoading(false)
  }
}

const mapGroupCode = (groupName: string) => {
  const map: Record<string, string> = {
    'Salários com Adicionais': 'SAL',
    Benefícios: 'BEN',
    Encargos: 'ENC',
    Temporários: 'TEMP',
    'Exames Ocupacionais': 'EXAM',
    Indenizações: 'IND',
    Outros: 'OUT',
    PLR: 'PLR',
    'Projeção Admissão': 'PROJ_ADM',
    'Projeção Rescisão': 'PROJ_RES'
  }

  return map[groupName] || groupName.toUpperCase().replace(/\s+/g, '_')
}

const mapDetailedSummaryResponse = (
  data: DetailedSummaryApiResponseProps,
  params: GetPeoplePreviewDetailsRequestProps
): DetailedSummaryResponseProps => {
  return {
    filiCode: params.filiCode,
    competenceDate: params.competenceDate,
    lastUpdateDate: undefined,
    status: undefined,
    grandTotal: data.totalCost ?? 0,
    generalAdjustAmount: data.generalAdjustAmount ?? 0,
    groups: (data.items || []).map((group, groupIndex) => {
      const groupCode = mapGroupCode(group.group)

      return {
        id: `${groupIndex}-${groupCode}`,
        code: groupCode,
        name: group.group,
        subtotal: group.totalAmount ?? 0,
        isEditable: true,
        items: (group.descriptions || []).map((item, itemIndex) => ({
          id: `${groupIndex}-${itemIndex}-${groupCode}`,
          description: item.name,
          accountCode: item.account || '',
          amount: item.amount ?? 0,
          groupCode,
          groupName: group.group,
          isEditable: item.allowEdit,
          isDiscount: item.isDiscount,
          dataSource: item.dataSource,
          observation: ''
        }))
      }
    })
  }
}

const getDetailedSummary = async (params: GetPeoplePreviewDetailsRequestProps) => {
  const { setDetailedSummaryData, setIsDetailedSummaryLoading } = usePeople.getState()

  setIsDetailedSummaryLoading(true)

  try {
    const data = await apiPeople.getDetailedSummary(params)
    setDetailedSummaryData(mapDetailedSummaryResponse(data, params))
  } catch (error) {
    const errorMessage =
      (error as AxiosError<{ errorMessage: string[] }>)?.response?.data?.errorMessage[0] ||
      'Falha ao obter o resumo detalhado do Pessoal!'

    toast.error(errorMessage, (error as AxiosError)?.status)
  } finally {
    setIsDetailedSummaryLoading(false)
  }
}

const editDetailedSummary = async (payload: EditDetailedSummaryPayloadProps & GetPeoplePreviewDetailsRequestProps) => {
  const { getDetailedSummary: getDetailedSummaryAction, getPeoplePreviewDetails: getPeoplePreviewDetailsAction } =
    usePeople.getState()

  try {
    await apiPeople.editDetailedSummary(payload)

    toast.success('Resumo Detalhado atualizado com sucesso!')

    // Recarrega o resumo detalhado
    await getDetailedSummaryAction({
      filiCode: payload.filiCode,
      competenceDate: payload.competenceDate,
      profitCenter: payload.profitCenter
    })

    // Recarrega os dados de preview (big numbers e previews semanais)
    await getPeoplePreviewDetailsAction({
      filiCode: payload.filiCode,
      competenceDate: payload.competenceDate,
      profitCenter: payload.profitCenter
    })
  } catch (error) {
    const errorMessage =
      (error as AxiosError<{ errorMessage: string[] }>)?.response?.data?.errorMessage[0] ||
      'Não foi possível editar o Resumo Detalhado. Tente novamente'

    toast.error(errorMessage, (error as AxiosError)?.status)
    throw error
  }
}

const getCollaborators = async (params: GetPeoplePreviewDetailsRequestProps) => {
  const { setCollaboratorsData, setIsCollaboratorsLoading } = usePeople.getState()

  setIsCollaboratorsLoading(true)

  try {
    const data = await apiPeople.getCollaborators(params)
    setCollaboratorsData(data)
  } catch (error) {
    const errorMessage =
      (error as AxiosError<{ errorMessage: string[] }>)?.response?.data?.errorMessage[0] ||
      'Falha ao obter os dados de colaboradores!'

    toast.error(errorMessage, (error as AxiosError)?.status)
  } finally {
    setIsCollaboratorsLoading(false)
  }
}

const getCollaboratorsList = async (params: GetCollaboratorsListRequestProps) => {
  const { setCollaboratorsListData, setIsCollaboratorsListLoading } = usePeople.getState()

  setIsCollaboratorsListLoading(true)

  try {
    const data = await apiPeople.getCollaboratorsList(params)
    setCollaboratorsListData(data)
  } catch (error) {
    const errorMessage =
      (error as AxiosError<{ errorMessage: string[] }>)?.response?.data?.errorMessage[0] ||
      'Falha ao obter a lista de colaboradores!'

    toast.error(errorMessage, (error as AxiosError)?.status)
  } finally {
    setIsCollaboratorsListLoading(false)
  }
}

const getCollaboratorDetails = async (
  params: GetCollaboratorDetailsRequestProps
): Promise<CollaboratorDetailsResponseProps | null> => {
  const { setCollaboratorDetailsData, setIsCollaboratorDetailsLoading } = usePeople.getState()

  setIsCollaboratorDetailsLoading(true)

  try {
    const data = await apiPeople.getCollaboratorDetails(params)
    setCollaboratorDetailsData(data)
    return data
  } catch (error) {
    const errorMessage =
      (error as AxiosError<{ errorMessage: string[] }>)?.response?.data?.errorMessage[0] ||
      'Falha ao obter os detalhes do colaborador!'

    toast.error(errorMessage, (error as AxiosError)?.status)
    return null
  } finally {
    setIsCollaboratorDetailsLoading(false)
  }
}

const getCollaboratorsTotal = async (params: GetCollaboratorsTotalRequestProps) => {
  const { setCollaboratorsTotalData, setIsCollaboratorsTotalLoading } = usePeople.getState()

  setIsCollaboratorsTotalLoading(true)

  try {
    const data = await apiPeople.getCollaboratorsTotal(params)
    setCollaboratorsTotalData(data)
  } catch (error) {
    const errorMessage =
      (error as AxiosError<{ errorMessage: string[] }>)?.response?.data?.errorMessage[0] ||
      'Falha ao obter o total de colaboradores!'

    toast.error(errorMessage, (error as AxiosError)?.status)
  } finally {
    setIsCollaboratorsTotalLoading(false)
  }
}

const getCollaboratorsIndicators = async (params: GetCollaboratorsIndicatorsRequestProps) => {
  const { setCollaboratorsIndicatorsData, setIsCollaboratorsIndicatorsLoading } = usePeople.getState()

  setIsCollaboratorsIndicatorsLoading(true)

  try {
    const data = await apiPeople.getCollaboratorsIndicators(params)
    setCollaboratorsIndicatorsData(data)
  } catch (error) {
    const errorMessage =
      (error as AxiosError<{ errorMessage: string[] }>)?.response?.data?.errorMessage[0] ||
      'Falha ao obter os indicadores de colaboradores!'

    toast.error(errorMessage, (error as AxiosError)?.status)
  } finally {
    setIsCollaboratorsIndicatorsLoading(false)
  }
}

const createRescissionSimulation = async (
  filiCode: string,
  payload: RescissionSimulationRequestProps
): Promise<RescissionSimulationResponseProps> => {
  const {
    getSimulations: getSimulationsAction,
    getDetailedSummary: getDetailedSummaryAction,
    getPeoplePreviewDetails: getPeoplePreviewDetailsAction,
    setIsRescissionSimulationLoading,
    getCollaboratorsList
  } = usePeople.getState()

  setIsRescissionSimulationLoading(true)

  try {
    const data = await apiPeople.createRescissionSimulation(filiCode, payload)
    toast.success('Simulação de rescisão criada com sucesso!')

    if (payload.referenceDate) {
      await getSimulationsAction(filiCode, payload.referenceDate)
      await new Promise((resolve) => setTimeout(resolve, 400))
      await getDetailedSummaryAction({
        filiCode,
        competenceDate: payload.referenceDate
      })
      await getPeoplePreviewDetailsAction({
        filiCode,
        competenceDate: payload.referenceDate
      })
    }

    await getCollaboratorsList({
      filiCode,
      referenceDate: payload.referenceDate
    })

    return data
  } catch (error) {
    const errorMessage =
      (error as AxiosError<{ errorMessage: string[] }>)?.response?.data?.errorMessage[0] ||
      'Falha ao criar simulação de rescisão!'

    toast.error(errorMessage, (error as AxiosError)?.status)
    throw error
  } finally {
    setIsRescissionSimulationLoading(false)
  }
}

const createAdmissionSimulation = async (
  filiCode: string,
  payload: AdmissionSimulationRequestProps
): Promise<SimulationListItemProps> => {
  try {
    const data = await apiPeople.createAdmissionSimulation(filiCode, payload)
    toast.success('Simulação de admissão criada com sucesso!')

    const {
      getSimulations: getSimulationsAction,
      getDetailedSummary: getDetailedSummaryAction,
      getPeoplePreviewDetails: getPeoplePreviewDetailsAction
    } = usePeople.getState()

    if (payload.referenceDate) {
      await getSimulationsAction(filiCode, payload.referenceDate)
      // Pequeno delay para o backend persistir antes de buscar o resumo atualizado
      await new Promise((resolve) => setTimeout(resolve, 400))
      await getDetailedSummaryAction({
        filiCode,
        competenceDate: payload.referenceDate
      })
      await getPeoplePreviewDetailsAction({
        filiCode,
        competenceDate: payload.referenceDate
      })
    }

    return data
  } catch (error) {
    const errorMessage =
      (error as AxiosError<{ errorMessage: string[] }>)?.response?.data?.errorMessage[0] ||
      'Falha ao criar simulação de admissão!'

    toast.error(errorMessage, (error as AxiosError)?.status)
    throw error
  }
}

const getSimulations = async (filiCode: string, referenceDate: string) => {
  const { setSimulationsData, setIsSimulationsLoading, setSimulationsCanEdit } = usePeople.getState()

  setIsSimulationsLoading(true)

  try {
    let formattedDate = referenceDate

    if (referenceDate && referenceDate.match(/^\d{4}-\d{2}-\d{2}$/)) {
      formattedDate = new Date(`${referenceDate}T00:00:00.000Z`).toISOString()
    } else if (referenceDate && referenceDate.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
      const isoDate = convertDateToIsoDate(referenceDate)
      if (isoDate) {
        formattedDate = new Date(`${isoDate}T00:00:00.000Z`).toISOString()
      }
    } else if (referenceDate && !referenceDate.includes('T')) {
      formattedDate = new Date(referenceDate).toISOString()
    }

    const data = await apiPeople.getSimulations(filiCode, formattedDate)
    setSimulationsData(data.items || [])
    setSimulationsCanEdit(data.canEdit ?? true)
  } catch (error) {
    const errorMessage =
      (error as AxiosError<{ errorMessage: string[] }>)?.response?.data?.errorMessage[0] ||
      'Falha ao obter lista de simulações!'

    toast.error(errorMessage, (error as AxiosError)?.status)
  } finally {
    setIsSimulationsLoading(false)
  }
}

const deleteSimulation = async (simulationId: number, filiCode: string, referenceDate: string): Promise<boolean> => {
  const {
    getSimulations: getSimulationsAction,
    getDetailedSummary: getDetailedSummaryAction,
    getPeoplePreviewDetails: getPeoplePreviewDetailsAction,
    getCollaboratorsList
  } = usePeople.getState()

  try {
    await apiPeople.deleteSimulation(simulationId)
    toast.success('Simulação excluída com sucesso!')

    await getSimulationsAction(filiCode, referenceDate)
    await getDetailedSummaryAction({
      filiCode,
      competenceDate: referenceDate
    })
    await getPeoplePreviewDetailsAction({
      filiCode,
      competenceDate: referenceDate
    })

    await getCollaboratorsList({
      filiCode,
      referenceDate: referenceDate
    })

    return true
  } catch (error) {
    const errorMessage =
      (error as AxiosError<{ errorMessage: string[] }>)?.response?.data?.errorMessage[0] ||
      'Falha ao excluir simulação!'

    toast.error(errorMessage, (error as AxiosError)?.status)
    return false
  }
}

const editRescissionSimulation = async (
  filiCode: string,
  simulationId: number,
  payload: apiPeople.EditRescissionSimulationRequestProps,
  referenceDate: string
): Promise<SimulationListItemProps> => {
  const {
    getSimulations: getSimulationsAction,
    setIsRescissionSimulationLoading,
    getCollaboratorsList,
    getDetailedSummary: getDetailedSummaryAction,
    getPeoplePreviewDetails: getPeoplePreviewDetailsAction
  } = usePeople.getState()

  setIsRescissionSimulationLoading(true)

  try {
    const data = await apiPeople.editRescissionSimulation(filiCode, simulationId, payload)
    toast.success('Simulação de rescisão atualizada com sucesso!')

    await getSimulationsAction(filiCode, referenceDate)
    await getDetailedSummaryAction({
      filiCode,
      competenceDate: referenceDate
    })
    await getPeoplePreviewDetailsAction({
      filiCode,
      competenceDate: referenceDate
    })
    await getCollaboratorsList({
      filiCode,
      referenceDate: referenceDate
    })

    return data
  } catch (error) {
    const errorMessage =
      (error as AxiosError<{ errorMessage: string[] }>)?.response?.data?.errorMessage[0] ||
      'Falha ao atualizar simulação de rescisão!'

    toast.error(errorMessage, (error as AxiosError)?.status)
    throw error
  } finally {
    setIsRescissionSimulationLoading(false)
  }
}

const editAdmissionSimulation = async (
  filiCode: string,
  simulationId: number,
  payload: apiPeople.EditAdmissionSimulationRequestProps,
  referenceDate: string
): Promise<SimulationListItemProps> => {
  try {
    const data = await apiPeople.editAdmissionSimulation(filiCode, simulationId, payload)
    toast.success('Simulação de admissão atualizada com sucesso!')

    const {
      getSimulations: getSimulationsAction,
      getDetailedSummary: getDetailedSummaryAction,
      getPeoplePreviewDetails: getPeoplePreviewDetailsAction
    } = usePeople.getState()
    await getSimulationsAction(filiCode, referenceDate)
    await new Promise((resolve) => setTimeout(resolve, 400))
    await getDetailedSummaryAction({
      filiCode,
      competenceDate: referenceDate
    })
    await getPeoplePreviewDetailsAction({
      filiCode,
      competenceDate: referenceDate
    })

    return data
  } catch (error) {
    const errorMessage =
      (error as AxiosError<{ errorMessage: string[] }>)?.response?.data?.errorMessage[0] ||
      'Falha ao atualizar simulação de admissão!'

    toast.error(errorMessage, (error as AxiosError)?.status)
    throw error
  }
}

const generalAdjustPeople = async (payload: GeneralAdjustPayloadProps): Promise<boolean> => {
  const { setIsGeneralAdjustLoading, getDetailedSummary: getDetailedSummaryAction, getPeoplePreviewDetails: getPeoplePreviewDetailsAction } =
    usePeople.getState()

  setIsGeneralAdjustLoading(true)

  try {
    const { filiCode, referenceDate, ...adjustmentFields } = payload

    await apiPeople.generalAdjustPeople(filiCode, {
      referenceDate,
      ...adjustmentFields
    })

    toast.success('Ajuste geral de prévia realizado com sucesso!')

    await getDetailedSummaryAction({ filiCode, competenceDate: referenceDate })
    await getPeoplePreviewDetailsAction({ filiCode, competenceDate: referenceDate })

    return true
  } catch (error) {
    const errorMessage =
      (error as AxiosError<{ errorMessage: string[] }>)?.response?.data?.errorMessage[0] ||
      'Não foi possível realizar o ajuste geral. Tente novamente'

    toast.error(errorMessage, (error as AxiosError)?.status)
    return false
  } finally {
    setIsGeneralAdjustLoading(false)
  }
}

const releasePreviewPeople = async (payload: { filiCode: string; competenceDate: string }): Promise<boolean> => {
  const { setIsReleasePreviewPeopleLoading } = usePeople.getState()

  setIsReleasePreviewPeopleLoading(true)

  try {
    await apiPeople.releasePreviewPeople({
      filiCode: payload.filiCode,
      competenceDate: payload.competenceDate
    })

    toast.success('Prévia liberada com sucesso!')

    const { getPeoplePreviewDetails: getPeoplePreviewDetailsAction } = usePeople.getState()
    await getPeoplePreviewDetailsAction({
      filiCode: payload.filiCode,
      competenceDate: payload.competenceDate
    })

    return true
  } catch (error) {
    toast.error('Não foi possível liberar a prévia. Tente novamente', (error as AxiosError)?.status)
    return false
  } finally {
    setIsReleasePreviewPeopleLoading(false)
  }
}

const reversePreviewPeople = async (payload: { filiCode: string; competenceDate: string }): Promise<boolean> => {
  const { setIsReversePreviewPeopleLoading } = usePeople.getState()

  setIsReversePreviewPeopleLoading(true)

  try {
    await apiPeople.reversePreviewPeople({
      filiCode: payload.filiCode,
      competenceDate: payload.competenceDate
    })

    toast.success('Prévia revertida com sucesso!')

    const { getPeoplePreviewDetails: getPeoplePreviewDetailsAction } = usePeople.getState()
    await getPeoplePreviewDetailsAction({
      filiCode: payload.filiCode,
      competenceDate: payload.competenceDate
    })

    return true
  } catch (error) {
    toast.error('Não foi possível reverter a prévia. Tente novamente', (error as AxiosError)?.status)
    return false
  } finally {
    setIsReversePreviewPeopleLoading(false)
  }
}

export {
  getPeoplePreviewDetails,
  getDetailedSummary,
  editDetailedSummary,
  getCollaborators,
  getCollaboratorsList,
  getCollaboratorDetails,
  getCollaboratorsTotal,
  getCollaboratorsIndicators,
  createRescissionSimulation,
  createAdmissionSimulation,
  getSimulations,
  deleteSimulation,
  editRescissionSimulation,
  editAdmissionSimulation,
  generalAdjustPeople,
  releasePreviewPeople,
  reversePreviewPeople
}
